import { Controller } from "@hotwired/stimulus"

// ISO 17636 indication types (common weld defects) - must match RtIndication::INDICATION_TYPES
const INDICATION_TYPES = [
  'Crack',
  'Porosity - Single',
  'Porosity - Cluster',
  'Porosity - Distributed',
  'Lack of Fusion',
  'Lack of Penetration',
  'Slag Inclusion',
  'Tungsten Inclusion',
  'Undercut',
  'Burn Through',
  'Incomplete Root Penetration',
  'Excess Penetration',
  'Root Concavity',
  'Root Convexity'
]

// ISO classification types - must match RtIndication::CLASSIFICATIONS
const CLASSIFICATIONS = [
  'Linear',    // Length > 3× width (cracks, lack of fusion, etc.)
  'Rounded',   // Length ≤ 3× width (porosity, inclusions)
  'Planar'     // Surface-breaking defects
]

// ISO 6520-1 standard defect classification codes - must match RtIndication::ISO_REFERENCES
const ISO_REFERENCES = [
  'ISO 6520-1: 100 - Cracks',
  'ISO 6520-1: 200 - Cavities/Porosity',
  'ISO 6520-1: 300 - Solid inclusions',
  'ISO 6520-1: 400 - Lack of fusion/penetration',
  'ISO 6520-1: 401 - Lack of side wall fusion',
  'ISO 6520-1: 402 - Lack of inter-run fusion',
  'ISO 6520-1: 500 - Imperfect shape',
  'ISO 6520-1: 600 - Miscellaneous imperfections'
]

// Acceptance decision options
const ACCEPTANCE_DECISIONS = ['Accepted', 'Rejected', 'Repair']

/**
 * RT Welds Tree Controller
 * Manages the unified welds + results tree view with expandable rows
 * Handles weld and indication CRUD operations with nested data
 */
export default class extends Controller {
  static targets = [
    "reportId",
    "taskId",
    "alertBox",
    "weldsContainer",
    "weldsTableBody",
    "weldRow",
    "expandedRow",
    "expandedContent",
    "expandIcon",
    "indicationsContent",
    "indicationsTableBody",
    "indicationCount",
    "newWeldForm",
    "newWeldNo",
    "newImageRef",
    "newWelderIds",
    "newDiameter",
    "newWallThickness",
    "newExposureId",
    "newOptionalHeader",
    "newSensitivity",
    "newDensity",
    "newDefectsLength",
    "newResults",
    "newRetake",
    "newComments",
    "newArea",
    "addWeldButton",
    "saveWeldButton",
    "createWeldButton",
    "emptyState"
  ]

  static values = {
    reportId: Number,
    taskId: Number
  }

  connect() {
    console.log('[rt-welds-tree] Controller connected')
    this.weldIdToDelete = null
    this.indicationToDelete = { weldId: null, indicationId: null }
    this.initializeSelect2()

    // Listen for tab change events to refresh exposure options when switching to welds tab
    // Note: We listen on document because the tabs:change event bubbles UP from the tabs controller,
    // and this controller is a CHILD of the tabs element (events bubble up, not down)
    this.boundHandleTabChange = this.handleTabChange.bind(this)
    document.addEventListener('tabs:change', this.boundHandleTabChange)
  }

  disconnect() {
    console.log('[rt-welds-tree] Controller disconnected')

    // Remove tab change event listener from document
    if (this.boundHandleTabChange) {
      document.removeEventListener('tabs:change', this.boundHandleTabChange)
    }

    // Destroy Select2 instances to prevent memory leaks
    if (window.jQuery && window.jQuery.fn.select2) {
      try {
        // Destroy Select2 on new weld form selects
        if (this.hasNewWelderIdsTarget) {
          window.jQuery(this.newWelderIdsTarget).select2('destroy')
        }
        if (this.hasNewExposureIdTarget) {
          window.jQuery(this.newExposureIdTarget).select2('destroy')
        }
        // Destroy Select2 on any expanded row selects
        this.element.querySelectorAll('.select2-hidden-accessible').forEach(select => {
          try {
            window.jQuery(select).select2('destroy')
          } catch (e) {
            // Ignore errors for already destroyed instances
          }
        })
      } catch (e) {
        console.warn('[rt-welds-tree] Error destroying Select2 instances:', e)
      }
    }
  }

  /**
   * Initialize Select2 dropdowns
   */
  initializeSelect2() {
    if (window.jQuery && window.jQuery.fn.select2) {
      // Only initialize Select2 in new weld form - NOT in weld rows
      // Weld rows are initialized when expanded via initializeSelect2ForRow()

      if (this.hasNewWelderIdsTarget) {
        window.jQuery(this.newWelderIdsTarget).select2({
          placeholder: 'Select welder(s)',
          allowClear: true,
          width: '100%'
        })
      }

      if (this.hasNewExposureIdTarget) {
        window.jQuery(this.newExposureIdTarget).select2({
          placeholder: 'Select exposure setup',
          allowClear: true,
          width: '100%'
        })
      }
    }
  }

  /**
   * Handle click on weld row to expand/collapse
   * Ignores clicks on action buttons (edit, delete)
   */
  handleRowClick(event) {
    // Ignore clicks on buttons or links within the row
    const target = event.target
    const isButton = target.closest('button') || target.closest('a') || target.tagName === 'BUTTON' || target.tagName === 'A'

    if (isButton) {
      return // Let the button handle its own click
    }

    event.preventDefault()
    const weldRow = event.currentTarget
    const weldId = weldRow.dataset.weldId

    this.toggleExpandById(weldId)
  }

  /**
   * Toggle expand/collapse of weld row
   */
  toggleExpand(event) {
    event.preventDefault()
    event.stopPropagation() // Prevent row click from also firing
    const weldId = event.currentTarget.dataset.weldId
    this.toggleExpandById(weldId)
  }

  /**
   * Toggle expand/collapse by weld ID
   */
  toggleExpandById(weldId) {
    const expandedRow = this.element.querySelector(`tr.weld-expanded-row[data-weld-id="${weldId}"]`)
    const weldRow = this.element.querySelector(`tr.weld-row[data-weld-id="${weldId}"]`)
    const button = this.element.querySelector(`.toggle-expand[data-weld-id="${weldId}"]`)

    if (expandedRow) {
      const isExpanded = !expandedRow.classList.contains('d-none')

      if (isExpanded) {
        // Collapse
        expandedRow.classList.add('d-none')
        if (button) button.classList.remove('expanded')
        weldRow.classList.remove('expanded')
      } else {
        // Expand
        expandedRow.classList.remove('d-none')
        if (button) button.classList.add('expanded')
        weldRow.classList.add('expanded')

        // Re-initialize Select2 for this expanded row
        this.initializeSelect2ForRow(expandedRow)
      }
    }
  }

  /**
   * Initialize Select2 for a specific expanded row
   */
  initializeSelect2ForRow(row) {
    if (window.jQuery && window.jQuery.fn.select2) {
      // Initialize Select2 for welder multi-select, checking if already initialized
      row.querySelectorAll('.select2-welder').forEach(element => {
        const $el = window.jQuery(element)
        if (!$el.data('select2')) {
          $el.select2({
            placeholder: 'Select welder(s)',
            allowClear: true,
            width: '100%'
          })
        }
      })

      // Initialize Select2 for exposure dropdowns, checking if already initialized
      row.querySelectorAll('.select2-exposure').forEach(element => {
        const $el = window.jQuery(element)
        if (!$el.data('select2')) {
          $el.select2({
            placeholder: 'Select exposure',
            allowClear: true,
            width: '100%'
          })
        }
      })
    }
  }

  /**
   * Handle tab change event from tabs controller
   * Refreshes exposure options when switching to welds tab
   * @param {CustomEvent} event - Tab change event
   */
  handleTabChange(event) {
    const { panel } = event.detail || {}

    // Only refresh if switching to welds panel
    if (panel && panel.id === 'welds-panel') {
      console.log('[rt-welds-tree] Switching to welds tab, refreshing exposure options')
      this.refreshExposureOptions()
    }
  }

  /**
   * Refresh exposure options in all exposure dropdowns
   * Fetches fresh exposure list from server and updates Select2 dropdowns
   */
  async refreshExposureOptions() {
    try {
      console.log('[rt-welds-tree] Fetching fresh exposure options from server')

      const response = await fetch(
        `/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/radiography_exposures.json`,
        {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': this.csrfToken
          }
        }
      )

      if (!response.ok) {
        console.error('[rt-welds-tree] Failed to fetch exposures:', response.status)
        return
      }

      const data = await response.json()
      console.log('[rt-welds-tree] Received', data.exposures.length, 'exposures')

      // Update all exposure dropdowns with fresh options
      this.updateExposureDropdowns(data.exposures)

    } catch (error) {
      console.error('[rt-welds-tree] Error refreshing exposure options:', error)
    }
  }

  /**
   * Update all exposure Select2 dropdowns with new options
   * Preserves currently selected values where possible
   * @param {Array} exposures - Array of exposure objects from server
   */
  updateExposureDropdowns(exposures) {
    // Build options HTML with setup_code and material
    let optionsHtml = '<option value="">Select Exposure Setup</option>'
    exposures.forEach(exposure => {
      const displayText = `${exposure.setup_code} - ${exposure.material || 'N/A'}`
      optionsHtml += `<option value="${exposure.id}">${this.escapeHtml(displayText)}</option>`
    })

    // Update the new weld form exposure dropdown
    if (this.hasNewExposureIdTarget) {
      const $newExposureSelect = window.jQuery(this.newExposureIdTarget)
      const currentNewValue = $newExposureSelect.val()

      // Destroy Select2, update options, reinitialize
      if ($newExposureSelect.data('select2')) {
        $newExposureSelect.select2('destroy')
      }
      $newExposureSelect.html(optionsHtml)

      // Restore previous value if it still exists
      if (currentNewValue && exposures.find(e => e.id == currentNewValue)) {
        $newExposureSelect.val(currentNewValue)
      }

      $newExposureSelect.select2({
        placeholder: 'Select exposure setup',
        allowClear: true,
        width: '100%'
      })

      console.log('[rt-welds-tree] Updated new weld form exposure dropdown')
    }

    // Update all expanded row exposure dropdowns
    const exposureSelects = this.element.querySelectorAll('.select2-exposure')
    exposureSelects.forEach(select => {
      const $select = window.jQuery(select)
      const currentValue = $select.val()

      // Destroy Select2, update options, reinitialize
      if ($select.data('select2')) {
        $select.select2('destroy')
      }
      $select.html(optionsHtml)

      // Restore previous value if it still exists
      if (currentValue && exposures.find(e => e.id == currentValue)) {
        $select.val(currentValue)
      }

      $select.select2({
        placeholder: 'Select exposure',
        allowClear: true,
        width: '100%'
      })
    })

    console.log('[rt-welds-tree] Updated', exposureSelects.length, 'exposure dropdowns in expanded rows')
  }

  /**
   * Toggle indications section visibility
   */
  toggleIndications(event) {
    event.preventDefault()
    const weldId = event.currentTarget.dataset.weldId
    const content = this.element.querySelector(`.indications-content[data-weld-id="${weldId}"]`)
    const icon = this.element.querySelector(`.indication-toggle-icon[data-weld-id="${weldId}"]`)

    if (content) {
      content.classList.toggle('collapsed')
    }
    if (icon) {
      icon.classList.toggle('collapsed')
    }
  }

  /**
   * Show new weld form
   */
  addNewWeld(event) {
    event.preventDefault()
    console.log('[rt-welds-tree] Show new weld form')

    if (this.hasNewWeldFormTarget) {
      this.newWeldFormTarget.classList.remove('d-none')

      // Initialize Select2 in new weld form
      if (window.jQuery && window.jQuery.fn.select2) {
        window.jQuery(this.newWeldFormTarget).find('.select2').select2({
          allowClear: true,
          width: '100%'
        })
      }

      // Scroll to form
      this.newWeldFormTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  /**
   * Cancel new weld creation
   */
  cancelNewWeld(event) {
    event.preventDefault()
    console.log('[rt-welds-tree] Cancel new weld')

    if (this.hasNewWeldFormTarget) {
      this.newWeldFormTarget.classList.add('d-none')
      this.clearNewWeldForm()
    }
  }

  /**
   * Clear new weld form fields
   */
  clearNewWeldForm() {
    if (this.hasNewWeldNoTarget) this.newWeldNoTarget.value = ''
    if (this.hasNewImageRefTarget) this.newImageRefTarget.value = ''
    if (this.hasNewDiameterTarget) this.newDiameterTarget.value = ''
    if (this.hasNewWallThicknessTarget) this.newWallThicknessTarget.value = ''
    
    if (this.hasNewSensitivityTarget) this.newSensitivityTarget.value = ''
    if (this.hasNewDensityTarget) this.newDensityTarget.value = ''
    if (this.hasNewDefectsLengthTarget) this.newDefectsLengthTarget.value = ''
    if (this.hasNewCommentsTarget) this.newCommentsTarget.value = ''
    if (this.hasNewRetakeTarget) this.newRetakeTarget.checked = false
    if (this.hasNewAreaTarget) this.newAreaTarget.value = ''

    // Clear Select2 dropdowns
    if (window.jQuery) {
      if (this.hasNewWelderIdsTarget) {
        window.jQuery(this.newWelderIdsTarget).val([]).trigger('change')
      }
      if (this.hasNewExposureIdTarget) {
        window.jQuery(this.newExposureIdTarget).val('').trigger('change')
      }
      if (this.hasNewResultsTarget) {
        window.jQuery(this.newResultsTarget).val('').trigger('change')
      }
    }
  }

  /**
   * Create new weld
   */
  async createWeld(event) {
    event.preventDefault()
    console.log('[rt-welds-tree] Create new weld')

    // Validate required fields
    const weldNo = this.hasNewWeldNoTarget ? this.newWeldNoTarget.value.trim() : ''
    const exposureId = this.hasNewExposureIdTarget ? this.newExposureIdTarget.value : ''

    if (!weldNo) {
      this.showError('Weld number is required')
      if (this.hasNewWeldNoTarget) this.newWeldNoTarget.focus()
      return
    }

    if (!exposureId) {
      this.showError('Exposure setup is required')
      return
    }

    // Collect form data
    const weldData = this.collectNewWeldData()

    console.log('[rt-welds-tree] New weld data:', weldData)

    // Disable create button
    if (this.hasCreateWeldButtonTarget) {
      this.createWeldButtonTarget.disabled = true
      this.createWeldButtonTarget.innerHTML = '<i class="fa fa-spinner fa-spin mr-1"></i> Creating...'
    }

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/welds`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ weld: weldData })
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds-tree] Weld created successfully:', data)

        // Dynamically add the new weld row to the table
        this.addWeldRowToTable(data.weld)

        // Hide and reset the new weld form
        if (this.hasNewWeldFormTarget) {
          this.newWeldFormTarget.classList.add('d-none')
          this.clearNewWeldForm()
        }

        // Show success message
        this.showSuccess('Weld created successfully')
      } else {
        console.error('[rt-welds-tree] Create failed:', data)
        this.showError(data.message || 'Failed to create weld')
      }
    } catch (error) {
      console.error('[rt-welds-tree] Network error:', error)
      this.showError('Network error. Please try again.')
    } finally {
      if (this.hasCreateWeldButtonTarget) {
        this.createWeldButtonTarget.disabled = false
        this.createWeldButtonTarget.innerHTML = '<i class="fa fa-plus mr-1"></i> Create Weld'
      }
    }
  }

  /**
   * Collect data from new weld form
   */
  collectNewWeldData() {
    let welderIds = []
    if (this.hasNewWelderIdsTarget) {
      if (window.jQuery && window.jQuery(this.newWelderIdsTarget).data('select2')) {
        welderIds = window.jQuery(this.newWelderIdsTarget).val() || []
      } else {
        welderIds = Array.from(this.newWelderIdsTarget.selectedOptions).map(o => o.value)
      }
    }

    return {
      weld_no: this.hasNewWeldNoTarget ? this.newWeldNoTarget.value : '',
      image_ref: this.hasNewImageRefTarget ? this.newImageRefTarget.value : '',
      material: this.hasNewMaterialTarget ? this.newMaterialTarget.value : '',
      welder: welderIds,
      diameter: this.hasNewDiameterTarget ? this.newDiameterTarget.value : '',
      wall_thickness: this.hasNewWallThicknessTarget ? this.newWallThicknessTarget.value : '',
      radiography_exposure_id: this.hasNewExposureIdTarget ? this.newExposureIdTarget.value : '',

      sensitivity: this.hasNewSensitivityTarget ? this.newSensitivityTarget.value : '',
      density: this.hasNewDensityTarget ? this.newDensityTarget.value : '',
      defects_length: this.hasNewDefectsLengthTarget ? this.newDefectsLengthTarget.value : '',
      results: this.hasNewResultsTarget ? this.newResultsTarget.value : '',
      retake: this.hasNewRetakeTarget ? this.newRetakeTarget.checked : false,
      comments: this.hasNewCommentsTarget ? this.newCommentsTarget.value : '',
      area: this.hasNewAreaTarget ? this.newAreaTarget.value : ''
    }
  }

  /**
   * Edit weld - expand the row for editing
   */
  editWeld(event) {
    event.preventDefault()
    const weldId = event.currentTarget.dataset.weldId
    console.log('[rt-welds-tree] Edit weld:', weldId)

    // Find and expand the weld row
    const expandButton = this.element.querySelector(`.toggle-expand[data-weld-id="${weldId}"]`)
    const expandedRow = this.element.querySelector(`tr.weld-expanded-row[data-weld-id="${weldId}"]`)

    if (expandedRow && expandedRow.classList.contains('d-none')) {
      // Trigger expand
      if (expandButton) {
        expandButton.click()
      }
    }

    // Scroll to the expanded content
    setTimeout(() => {
      const content = this.element.querySelector(`.weld-expanded-content[data-weld-id="${weldId}"]`)
      if (content) {
        content.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  /**
   * Save weld and its indications
   */
  async saveWeld(event) {
    event.preventDefault()
    const weldId = event.currentTarget.dataset.weldId
    console.log('[rt-welds-tree] Save weld:', weldId)

    const expandedContent = this.element.querySelector(`.weld-expanded-content[data-weld-id="${weldId}"]`)
    if (!expandedContent) {
      this.showError('Could not find weld data')
      return
    }

    // Collect weld data from the expanded form
    const weldData = this.collectWeldData(expandedContent, weldId)

    // Collect indication data
    const indicationsData = this.collectIndicationsData(expandedContent, weldId)

    console.log('[rt-welds-tree] Weld data:', weldData)
    console.log('[rt-welds-tree] Indications data:', indicationsData)

    // Disable save button
    const saveButton = event.currentTarget
    saveButton.disabled = true
    saveButton.innerHTML = '<i class="fa fa-spinner fa-spin mr-1"></i> Saving...'

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/welds/${weldId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            weld: {
              ...weldData,
              rt_indications_attributes: indicationsData
            }
          })
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds-tree] Weld saved successfully:', data)
        this.handleSaveSuccess(weldId, data)
      } else {
        console.error('[rt-welds-tree] Save failed:', data)
        this.showError(data.message || 'Failed to save weld')
      }
    } catch (error) {
      console.error('[rt-welds-tree] Network error:', error)
      this.showError('Network error. Please try again.')
    } finally {
      saveButton.disabled = false
      saveButton.innerHTML = '<i class="fa fa-save mr-1"></i> Save Weld'
    }
  }

  /**
   * Collect weld data from expanded content
   */
  collectWeldData(container, weldId) {
    // Get welder IDs from Select2
    let welderIds = []
    const welderSelect = container.querySelector('.select2-welder')
    if (welderSelect) {
      if (window.jQuery && window.jQuery(welderSelect).data('select2')) {
        welderIds = window.jQuery(welderSelect).val() || []
      } else {
        welderIds = Array.from(welderSelect.selectedOptions).map(o => o.value)
      }
    }

    // Get exposure ID from Select2
    let exposureId = ''
    const exposureSelect = container.querySelector('.select2-exposure')
    if (exposureSelect) {
      if (window.jQuery && window.jQuery(exposureSelect).data('select2')) {
        exposureId = window.jQuery(exposureSelect).val() || ''
      } else {
        exposureId = exposureSelect.value
      }
    }

    return {
      weld_no: container.querySelector('.weld-no-input')?.value || '',
      image_ref: container.querySelector('.image-ref-input')?.value || '',
      material: container.querySelector('.material-input')?.value || '',
      welder: welderIds,
      diameter: container.querySelector('.diameter-input')?.value || '',
      wall_thickness: container.querySelector('.wall-thickness-input')?.value || '',
      radiography_exposure_id: exposureId,

      sensitivity: container.querySelector('.sensitivity-input')?.value || '',
      density: container.querySelector('.density-input')?.value || '',
      defects_length: container.querySelector('.defects-length-input')?.value || '',
      results: container.querySelector('.results-input')?.value || '',
      retake: container.querySelector('.retake-input')?.checked || false,
      comments: container.querySelector('.comments-input')?.value || '',
      area: container.querySelector('.area-input')?.value || ''
    }
  }

  /**
   * Collect indications data from expanded content
   */
  collectIndicationsData(container, weldId) {
    const indicationsData = []
    const indicationRows = container.querySelectorAll('.indication-row')

    indicationRows.forEach(row => {
      const indicationId = row.dataset.indicationId
      indicationsData.push({
        id: indicationId,
        location_identification: row.querySelector('.indication-location')?.value || '',
        indication_classification: row.querySelector('.indication-classification')?.value || '',
        indication_type: row.querySelector('.indication-type')?.value || '',
        indication_size: row.querySelector('.indication-size')?.value || '',
        iso_reference: row.querySelector('.indication-iso-reference')?.value || '',
        acceptance_decision: row.querySelector('.indication-acceptance')?.value || ''
      })
    })

    return indicationsData
  }

  /**
   * Handle successful save
   */
  handleSaveSuccess(weldId, data) {
    const weld = data.weld

    // Update the collapsed row with new data
    const weldRow = this.element.querySelector(`tr.weld-row[data-weld-id="${weldId}"]`)
    if (weldRow && weld) {
      this.updateWeldRowDisplay(weldRow, weld)

      // Add highlight animation
      weldRow.classList.add('just-saved')
      setTimeout(() => weldRow.classList.remove('just-saved'), 2000)
    }

    // Collapse the expanded row
    this.toggleExpandById(weldId)

    // Show success message
    this.showSuccess('Weld saved successfully')
  }

  /**
   * Add a new weld row to the table dynamically (without page reload)
   * Handles both the collapsed row and the expanded row
   */
  addWeldRowToTable(weld) {
    console.log('[rt-welds-tree] Adding weld row to table:', weld)

    // Hide empty state if visible
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('d-none')
    }

    // Check if table exists, if not we need to create it
    let tableBody = this.hasWeldsTableBodyTarget ? this.weldsTableBodyTarget : null

    if (!tableBody) {
      // Table doesn't exist yet - create the table structure
      this.createWeldsTable()
      tableBody = this.weldsTableBodyTarget
    }

    if (!tableBody) {
      console.error('[rt-welds-tree] Could not find or create welds table body')
      return
    }

    // Create the collapsed weld row HTML
    const collapsedRowHtml = this.createWeldRow(weld)

    // Create the expanded weld row HTML
    const expandedRowHtml = this.createExpandedWeldRow(weld)

    // Insert both rows at the end of the table body
    tableBody.insertAdjacentHTML('beforeend', collapsedRowHtml + expandedRowHtml)

    // Find the newly inserted row and highlight it
    const newRow = tableBody.querySelector(`tr.weld-row[data-weld-id="${weld.id}"]`)
    if (newRow) {
      newRow.classList.add('just-saved')
      setTimeout(() => newRow.classList.remove('just-saved'), 2000)

      // Scroll to the new row
      newRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  /**
   * Create the welds table structure if it doesn't exist
   */
  createWeldsTable() {
    const weldsContainer = this.hasWeldsContainerTarget ? this.weldsContainerTarget : null

    if (!weldsContainer) {
      // Find or create the container
      const cardBody = this.element.querySelector('.card-body.p-0')
      if (!cardBody) {
        console.error('[rt-welds-tree] Could not find card body for welds table')
        return
      }

      const tableHtml = `
        <div class="table-responsive" data-rt-welds-tree-target="weldsContainer">
          <table class="table table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width: 40px;"></th>
                <th>Weld #</th>
                <th>Welder(s)</th>
                <th>Dia (mm)</th>
                <th>t (mm)</th>
                <th>Image</th>
                <th>Exp.</th>
                <th>Result</th>
                <th class="text-center"><i class="fa fa-comment" title="Comments"></i></th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody data-rt-welds-tree-target="weldsTableBody">
            </tbody>
          </table>
        </div>
      `
      cardBody.innerHTML = tableHtml
    }
  }

  /**
   * Create HTML for the collapsed weld row
   */
  createWeldRow(weld) {
    // Determine result badge
    let resultBadge = '<span class="badge badge-secondary">Pending</span>'
    if (weld.results) {
      switch (weld.results) {
        case 'Accepted':
          resultBadge = `<span class="badge badge-success">${weld.results}</span>`
          break
        case 'Rejected':
          resultBadge = `<span class="badge badge-danger">${weld.results}</span>`
          break
        default:
          resultBadge = `<span class="badge badge-secondary">${weld.results}</span>`
      }
    }

    // Determine exposure badge
    let exposureBadge = '<span class="text-muted">-</span>'
    if (weld.radiography_exposure && weld.radiography_exposure.setup_code) {
      exposureBadge = `<span class="badge badge-dark">${this.escapeHtml(weld.radiography_exposure.setup_code)}</span>`
    }

    // Determine comments icon
    const commentsIcon = weld.comments
      ? `<i class="fa fa-comment text-info" title="${this.escapeHtml(weld.comments)}"></i>`
      : '<i class="fa fa-comment-o text-muted"></i>'

    // Welder names
    const welderNames = weld.welder_names || '<span class="text-muted">-</span>'

    return `
      <tr class="weld-row" data-weld-id="${weld.id}" data-rt-welds-tree-target="weldRow" data-action="click->rt-welds-tree#handleRowClick" style="cursor: pointer;">
        <td class="text-center">
          <button class="btn btn-link btn-sm p-0 toggle-expand" type="button" data-action="click->rt-welds-tree#toggleExpand" data-weld-id="${weld.id}">
            <i class="fa fa-chevron-right expand-icon" data-rt-welds-tree-target="expandIcon"></i>
          </button>
        </td>
        <td><strong>${this.escapeHtml(weld.weld_no || '-')}</strong></td>
        <td>${welderNames}</td>
        <td>${weld.diameter || '-'}</td>
        <td>${weld.wall_thickness || '-'}</td>
        <td>${this.escapeHtml(weld.material || weld.exposure_material || '-')}</td>
        <td>${exposureBadge}</td>
        <td>${resultBadge}</td>
        <td class="text-center">${commentsIcon}</td>
        <td class="text-center text-nowrap">
          <button class="btn btn-sm btn-outline-primary mr-1" type="button" data-action="click->rt-welds-tree#editWeld" data-weld-id="${weld.id}" title="Edit">
            <i class="fa fa-edit"></i>
          </button>
          <button class="btn btn-sm btn-outline-danger" type="button" data-action="click->rt-welds-tree#deleteWeld" data-weld-id="${weld.id}" title="Delete">
            <i class="fa fa-trash"></i>
          </button>
        </td>
      </tr>
    `
  }

  /**
   * Create HTML for the expanded weld row (detail view)
   */
  createExpandedWeldRow(weld) {
    // Get welder options from the new weld form (reuse existing options)
    let welderOptionsHtml = ''
    if (this.hasNewWelderIdsTarget) {
      const welderSelect = this.newWelderIdsTarget
      const selectedWelderIds = weld.welder || []
      Array.from(welderSelect.options).forEach(option => {
        const isSelected = selectedWelderIds.includes(option.value) || selectedWelderIds.includes(parseInt(option.value))
        welderOptionsHtml += `<option value="${option.value}"${isSelected ? ' selected' : ''}>${this.escapeHtml(option.text)}</option>`
      })
    }

    // Get exposure options from the new weld form (reuse existing options)
    let exposureOptionsHtml = '<option value="">Select Exposure Setup</option>'
    if (this.hasNewExposureIdTarget) {
      const exposureSelect = this.newExposureIdTarget
      Array.from(exposureSelect.options).forEach(option => {
        if (option.value) {
          const isSelected = weld.radiography_exposure_id == option.value
          exposureOptionsHtml += `<option value="${option.value}"${isSelected ? ' selected' : ''}>${this.escapeHtml(option.text)}</option>`
        }
      })
    }

    // Result options
    const resultOptions = ['', 'Accepted', 'Rejected']
    const resultOptionsHtml = resultOptions.map(r => {
      const isSelected = weld.results === r
      const label = r || 'Select Result'
      return `<option value="${r}"${isSelected ? ' selected' : ''}>${label}</option>`
    }).join('')

    return `
      <tr class="weld-expanded-row d-none" data-weld-id="${weld.id}" data-rt-welds-tree-target="expandedRow">
        <td colspan="10" style="padding: 0; background-color: #f8f9fa;">
          <div class="weld-expanded-content p-3" data-rt-welds-tree-target="expandedContent" data-weld-id="${weld.id}">
            <input type="hidden" class="weld-id-field" value="${weld.id}">

            <div class="row">
              <!-- Left Panel - Weld Details -->
              <div class="col-md-6">
                <div class="card h-100">
                  <div class="card-header bg-secondary text-white py-2">
                    <i class="fa fa-info-circle mr-2"></i>
                    Weld Details
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Weld No.</label>
                          <input class="form-control form-control-sm weld-no-input" type="text" value="${this.escapeHtml(weld.weld_no || '')}" data-field="weld_no">
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Image Reference</label>
                          <input class="form-control form-control-sm image-ref-input" type="text" value="${this.escapeHtml(weld.image_ref || '')}" placeholder="e.g., IMG-001" data-field="image_ref">
                          <input class="form-control form-control-sm material-input mt-2" type="text" value="${this.escapeHtml(weld.material || '')}" placeholder="Material, e.g., CS" data-field="material">
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Segment / Area</label>
                          <input class="form-control form-control-sm area-input" type="text" value="${this.escapeHtml(weld.area || '')}" placeholder="e.g., 22-48 cm, A/B/C, 0-500 mm" data-field="area">
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Welder(s)</label>
                          <select class="form-control form-control-sm select2-welder" multiple="multiple" style="width: 100%;" data-field="welder">
                            ${welderOptionsHtml}
                          </select>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Exposure Setup</label>
                          <select class="form-control form-control-sm select2-exposure" style="width: 100%;" data-field="radiography_exposure_id">
                            ${exposureOptionsHtml}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Diameter</label>
                          <div class="input-group input-group-sm">
                            <input class="form-control diameter-input" type="number" min="0" step="0.1" value="${weld.diameter || ''}" data-field="diameter">
                            <div class="input-group-append">
                              <span class="input-group-text">mm</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Wall Thickness</label>
                          <div class="input-group input-group-sm">
                            <input class="form-control wall-thickness-input" type="number" min="0" step="0.1" value="${weld.wall_thickness || ''}" data-field="wall_thickness">
                            <div class="input-group-append">
                              <span class="input-group-text">mm</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <!-- Right Panel - Result Summary -->
              <div class="col-md-6">
                <div class="card h-100">
                  <div class="card-header bg-info text-white py-2">
                    <i class="fa fa-check-circle mr-2"></i>
                    Result Summary
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Sensitivity (Ach IQI)</label>
                          <div class="input-group input-group-sm">
                            <div class="input-group-prepend">
                              <span class="input-group-text">W</span>
                            </div>
                            <input class="form-control sensitivity-input" type="text" value="${this.escapeHtml(weld.sensitivity || '')}" placeholder="e.g., 14" data-field="sensitivity">
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Gray Levels</label>
                          <input class="form-control form-control-sm density-input" type="text" value="${this.escapeHtml(weld.density || '')}" placeholder="Enter density" data-field="density">
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Total Defect Length</label>
                          <div class="input-group input-group-sm">
                            <input class="form-control defects-length-input" type="text" value="${this.escapeHtml(weld.defects_length || '')}" placeholder="Total length" data-field="defects_length">
                            <div class="input-group-append">
                              <span class="input-group-text">mm</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Result</label>
                          <select class="form-control form-control-sm results-input" data-field="results">
                            ${resultOptionsHtml}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-group mb-0">
                          <div class="custom-control custom-checkbox">
                            <input class="custom-control-input retake-input" type="checkbox" id="retake_weld_${weld.id}" ${weld.retake ? 'checked' : ''} data-field="retake">
                            <label class="custom-control-label" for="retake_weld_${weld.id}">
                              <strong>Retake Required</strong>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group mb-0">
                          <label class="font-weight-bold small">Comments</label>
                          <textarea class="form-control form-control-sm comments-input" rows="2" placeholder="Internal comments..." data-field="comments">${this.escapeHtml(weld.comments || '')}</textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Indications Section -->
            <div class="card mt-3">
              <div class="card-header bg-warning text-dark py-2 d-flex justify-content-between align-items-center" data-action="click->rt-welds-tree#toggleIndications" data-weld-id="${weld.id}" style="cursor: pointer;">
                <div class="indication-header">
                  <i class="fa fa-exclamation-triangle mr-2"></i>
                  Indications
                  <span class="badge badge-dark ml-2" data-rt-welds-tree-target="indicationCount" data-weld-id="${weld.id}">0</span>
                </div>
                <i class="fa fa-chevron-down indication-toggle-icon" data-weld-id="${weld.id}"></i>
              </div>
              <div class="card-body indications-content" data-rt-welds-tree-target="indicationsContent" data-weld-id="${weld.id}">
                <div class="text-center text-muted py-3 no-indications-message" data-weld-id="${weld.id}">
                  <i class="fa fa-check-circle mr-2"></i>
                  No indications recorded for this weld
                </div>

                <!-- Add Indication Form -->
                <div class="add-indication-form border rounded p-3 bg-light mt-2" data-weld-id="${weld.id}">
                  <h6 class="mb-3">
                    <i class="fa fa-plus-circle mr-2"></i>
                    Add New Indication
                  </h6>
                  <div class="row">
                    <div class="col-md-4 mb-2">
                      <div class="form-group mb-0">
                        <label class="small font-weight-bold">Location/ID</label>
                        <input class="form-control form-control-sm new-indication-location" type="text" placeholder="e.g., 100mm from start" data-weld-id="${weld.id}">
                      </div>
                    </div>
                    <div class="col-md-4 mb-2">
                      <div class="form-group mb-0">
                        <label class="small font-weight-bold">Classification</label>
                        <select class="form-control form-control-sm new-indication-classification" data-weld-id="${weld.id}">
                          <option value="">Select...</option>
                          ${CLASSIFICATIONS.map(c => `<option value="${c}">${c}</option>`).join('')}
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4 mb-2">
                      <div class="form-group mb-0">
                        <label class="small font-weight-bold">Indication Type</label>
                        <select class="form-control form-control-sm new-indication-type" data-weld-id="${weld.id}">
                          <option value="">Select type...</option>
                          ${INDICATION_TYPES.map(t => `<option value="${t}">${t}</option>`).join('')}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-4 mb-2">
                      <div class="form-group mb-0">
                        <label class="small font-weight-bold">Size</label>
                        <input class="form-control form-control-sm new-indication-size" type="text" placeholder="e.g., 20mm x 2mm" data-weld-id="${weld.id}">
                      </div>
                    </div>
                    <div class="col-md-4 mb-2">
                      <div class="form-group mb-0">
                        <label class="small font-weight-bold">ISO Reference</label>
                        <select class="form-control form-control-sm new-indication-iso" data-weld-id="${weld.id}">
                          <option value="">Select ISO Reference...</option>
                          ${ISO_REFERENCES.map(i => `<option value="${i}">${i}</option>`).join('')}
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4 mb-2">
                      <div class="form-group mb-0">
                        <label class="small font-weight-bold">Acceptance</label>
                        <select class="form-control form-control-sm new-indication-acceptance" data-weld-id="${weld.id}">
                          <option value="">Select...</option>
                          <option value="Accepted">Accepted</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Repair">Repair</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="text-right mt-2">
                    <button class="btn btn-sm btn-outline-info" type="button" data-action="click->rt-welds-tree#addIndication" data-weld-id="${weld.id}">
                      <i class="fa fa-plus mr-1"></i>
                      Add Indication
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="weld-actions mt-3 pt-3 border-top d-flex justify-content-end">
              <button class="btn btn-secondary mr-2" type="button" data-action="click->rt-welds-tree#cancelEdit" data-weld-id="${weld.id}">
                <i class="fa fa-times mr-1"></i>
                Cancel
              </button>
              <button class="btn btn-primary" type="button" data-action="click->rt-welds-tree#saveWeld" data-weld-id="${weld.id}" data-rt-welds-tree-target="saveWeldButton">
                <i class="fa fa-save mr-1"></i>
                Save Weld
              </button>
            </div>
          </div>
        </td>
      </tr>
    `
  }

  /**
   * Update the collapsed weld row display with new data
   */
  updateWeldRowDisplay(weldRow, weld) {
    const cells = weldRow.querySelectorAll('td')

    // Cell 0: Toggle button (skip)
    // Cell 1: Weld No
    const weldNoCell = cells[1]
    if (weldNoCell) {
      weldNoCell.innerHTML = `<strong>${weld.weld_no || '-'}</strong>`
    }

    // Cell 2: Welder(s)
    const welderCell = cells[2]
    if (welderCell) {
      if (weld.welder_names) {
        welderCell.textContent = weld.welder_names
      } else {
        welderCell.innerHTML = '<span class="text-muted">-</span>'
      }
    }

    // Cell 3: Diameter
    const diameterCell = cells[3]
    if (diameterCell) {
      diameterCell.textContent = weld.diameter || '-'
    }

    // Cell 4: Wall Thickness
    const wallThicknessCell = cells[4]
    if (wallThicknessCell) {
      wallThicknessCell.textContent = weld.wall_thickness || '-'
    }

    // Cell 5: Image Ref
    const imageRefCell = cells[5]
    if (imageRefCell) {
      imageRefCell.textContent = weld.image_ref || '-'
    }

    // Cell 6: Exposure setup code
    const exposureCell = cells[6]
    if (exposureCell) {
      if (weld.radiography_exposure && weld.radiography_exposure.setup_code) {
        exposureCell.innerHTML = `<span class="badge badge-dark">${weld.radiography_exposure.setup_code}</span>`
      } else {
        exposureCell.innerHTML = '<span class="text-muted">-</span>'
      }
    }

    // Cell 7: Results
    const resultsCell = cells[7]
    if (resultsCell) {
      let badgeClass = 'badge-secondary'
      let resultText = 'Pending'

      if (weld.results) {
        resultText = weld.results
        switch (weld.results) {
          case 'Accepted':
            badgeClass = 'badge-success'
            break
          case 'Rejected':
            badgeClass = 'badge-danger'
            break
        }
      }
      resultsCell.innerHTML = `<span class="badge ${badgeClass}">${resultText}</span>`
    }

    // Cell 8: Comments indicator
    const commentsCell = cells[8]
    if (commentsCell) {
      if (weld.comments) {
        commentsCell.innerHTML = `<i class="fa fa-comment text-info" title="${this.escapeHtml(weld.comments)}"></i>`
      } else {
        commentsCell.innerHTML = '<i class="fa fa-comment-o text-muted"></i>'
      }
    }

    // Cell 9: Actions (skip - no updates needed)
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Cancel editing a weld
   */
  cancelEdit(event) {
    event.preventDefault()
    const weldId = event.currentTarget.dataset.weldId
    console.log('[rt-welds-tree] Cancel edit for weld:', weldId)

    // Restore original values from the collapsed row display
    // Note: For a complete solution, we would need to store original values on expand
    // For now, just collapse the row - users can re-expand to see current server values

    // Collapse the expanded row
    this.toggleExpandById(weldId)
  }

  /**
   * Delete weld - show confirmation modal
   */
  deleteWeld(event) {
    event.preventDefault()
    const weldId = event.currentTarget.dataset.weldId
    this.weldIdToDelete = weldId

    console.log('[rt-welds-tree] Show delete confirmation for weld:', weldId)

    const modal = document.getElementById('deleteWeldModal')
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('show')
      } else {
        modal.classList.add('show')
        modal.style.display = 'block'
        document.body.classList.add('modal-open')
      }
    }
  }

  /**
   * Confirm delete weld
   */
  async confirmDeleteWeld(event) {
    event.preventDefault()

    if (!this.weldIdToDelete) {
      console.error('[rt-welds-tree] No weld ID to delete')
      return
    }

    const weldId = this.weldIdToDelete

    console.log('[rt-welds-tree] Deleting weld:', weldId)

    // Close modal
    this.closeModal('deleteWeldModal')

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/welds/${weldId}`,
        {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          }
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds-tree] Weld deleted successfully')
        // Reload page
        window.location.reload()
      } else {
        console.error('[rt-welds-tree] Delete failed:', data)
        this.showError(data.message || 'Failed to delete weld')
      }
    } catch (error) {
      console.error('[rt-welds-tree] Delete error:', error)
      this.showError('Network error. Please try again.')
    }

    this.weldIdToDelete = null
  }

  /**
   * Add indication to a weld
   */
  async addIndication(event) {
    event.preventDefault()
    const weldId = event.currentTarget.dataset.weldId
    console.log('[rt-welds-tree] Add indication to weld:', weldId)

    // Get form values
    const container = this.element.querySelector(`.add-indication-form[data-weld-id="${weldId}"]`)
    if (!container) {
      this.showError('Could not find add indication form')
      return
    }

    const location = container.querySelector('.new-indication-location')?.value || ''
    const classification = container.querySelector('.new-indication-classification')?.value || ''
    const type = container.querySelector('.new-indication-type')?.value || ''
    const size = container.querySelector('.new-indication-size')?.value || ''
    const iso = container.querySelector('.new-indication-iso')?.value || ''
    const acceptance = container.querySelector('.new-indication-acceptance')?.value || ''

    if (!location) {
      this.showError('Location/ID is required for indication')
      return
    }

    const indicationData = {
      location_identification: location,
      indication_classification: classification,
      indication_type: type,
      indication_size: size,
      iso_reference: iso,
      acceptance_decision: acceptance,
      weld_id: weldId
    }

    console.log('[rt-welds-tree] New indication data:', indicationData)

    // Disable add button during request
    const addButton = event.currentTarget
    addButton.disabled = true
    addButton.innerHTML = '<i class="fa fa-spinner fa-spin mr-1"></i> Adding...'

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/rt_indications`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ rt_indication: indicationData })
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds-tree] Indication added successfully:', data)

        // Add the new indication row to the DOM
        this.addIndicationRowToTable(weldId, data.indication)

        // Clear the add indication form
        this.clearAddIndicationForm(weldId)

        // Update indication count badge
        this.updateIndicationCount(weldId, 1)

        // Show success message
        this.showSuccess('Indication added successfully')
      } else {
        console.error('[rt-welds-tree] Add indication failed:', data)
        this.showError(data.message || 'Failed to add indication')
      }
    } catch (error) {
      console.error('[rt-welds-tree] Network error:', error)
      this.showError('Network error. Please try again.')
    } finally {
      addButton.disabled = false
      addButton.innerHTML = '<i class="fa fa-plus mr-1"></i> Add Indication'
    }
  }

  /**
   * Add a new indication row to the indications table
   * Creates the table if it doesn't exist yet
   */
  addIndicationRowToTable(weldId, indication) {
    const indicationsContent = this.element.querySelector(`.indications-content[data-weld-id="${weldId}"]`)
    if (!indicationsContent) {
      console.error('[rt-welds-tree] Could not find indications content for weld:', weldId)
      return
    }

    // Check if table exists, if not create it
    let tableBody = indicationsContent.querySelector(`tbody[data-rt-welds-tree-target="indicationsTableBody"][data-weld-id="${weldId}"]`)

    if (!tableBody) {
      // Table doesn't exist - create it and hide the "no indications" message
      this.createIndicationsTable(weldId, indicationsContent)
      tableBody = indicationsContent.querySelector(`tbody[data-rt-welds-tree-target="indicationsTableBody"][data-weld-id="${weldId}"]`)
    }

    // Hide "no indications" message if visible
    const noIndicationsMsg = indicationsContent.querySelector(`.no-indications-message[data-weld-id="${weldId}"]`)
    if (noIndicationsMsg) {
      noIndicationsMsg.style.display = 'none'
    }

    // Create the new row HTML
    const newRow = this.createIndicationRow(weldId, indication)

    // Insert the row into the table body
    tableBody.insertAdjacentHTML('beforeend', newRow)

    // Highlight the new row briefly
    const insertedRow = tableBody.querySelector(`tr.indication-row[data-indication-id="${indication.id}"]`)
    if (insertedRow) {
      insertedRow.classList.add('just-saved')
      setTimeout(() => insertedRow.classList.remove('just-saved'), 2000)
    }
  }

  /**
   * Create the indications table structure if it doesn't exist
   */
  createIndicationsTable(weldId, indicationsContent) {
    const tableHtml = `
      <div class="table-responsive">
        <table class="table table-sm table-bordered mb-3">
          <thead class="thead-light">
            <tr>
              <th>Location/ID</th>
              <th>Classification</th>
              <th>Type (ISO 5220-1)</th>
              <th>Size</th>
              <th>ISO Reference</th>
              <th>Acceptance</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody data-rt-welds-tree-target="indicationsTableBody" data-weld-id="${weldId}">
          </tbody>
        </table>
      </div>
    `

    // Insert before the add-indication-form
    const addForm = indicationsContent.querySelector(`.add-indication-form[data-weld-id="${weldId}"]`)
    if (addForm) {
      addForm.insertAdjacentHTML('beforebegin', tableHtml)
    } else {
      // Fallback: prepend to indications content
      indicationsContent.insertAdjacentHTML('afterbegin', tableHtml)
    }
  }

  /**
   * Create HTML for a single indication row
   */
  createIndicationRow(weldId, indication) {
    const classificationOptions = this.createSelectOptions(CLASSIFICATIONS, indication.indication_classification)
    const typeOptions = this.createSelectOptions(INDICATION_TYPES, indication.indication_type)
    const isoOptions = this.createSelectOptions(ISO_REFERENCES, indication.iso_reference)
    const acceptanceOptions = this.createSelectOptions(ACCEPTANCE_DECISIONS, indication.acceptance_decision)

    return `
      <tr class="indication-row" data-indication-id="${indication.id}">
        <td>
          <input class="form-control form-control-sm indication-location" type="text" value="${this.escapeHtml(indication.location_identification || '')}" data-field="location_identification">
        </td>
        <td>
          <select class="form-control form-control-sm indication-classification" data-field="indication_classification">
            <option value="">-</option>
            ${classificationOptions}
          </select>
        </td>
        <td>
          <select class="form-control form-control-sm indication-type" data-field="indication_type">
            <option value="">-</option>
            ${typeOptions}
          </select>
        </td>
        <td>
          <input class="form-control form-control-sm indication-size" type="text" value="${this.escapeHtml(indication.indication_size || '')}" data-field="indication_size">
        </td>
        <td>
          <select class="form-control form-control-sm indication-iso-reference" data-field="iso_reference">
            <option value="">-</option>
            ${isoOptions}
          </select>
        </td>
        <td>
          <select class="form-control form-control-sm indication-acceptance" data-field="acceptance_decision">
            <option value="">-</option>
            ${acceptanceOptions}
          </select>
        </td>
        <td class="text-center">
          <button class="btn btn-sm btn-outline-danger" type="button" data-action="click->rt-welds-tree#deleteIndication" data-indication-id="${indication.id}" data-weld-id="${weldId}" title="Remove">
            <i class="fa fa-times"></i>
          </button>
        </td>
      </tr>
    `
  }

  /**
   * Create HTML options for a select element
   */
  createSelectOptions(options, selectedValue) {
    return options.map(option => {
      const selected = option === selectedValue ? ' selected' : ''
      return `<option value="${this.escapeHtml(option)}"${selected}>${this.escapeHtml(option)}</option>`
    }).join('')
  }

  /**
   * Clear the add indication form fields
   */
  clearAddIndicationForm(weldId) {
    const container = this.element.querySelector(`.add-indication-form[data-weld-id="${weldId}"]`)
    if (!container) return

    // Clear text inputs
    const locationInput = container.querySelector('.new-indication-location')
    if (locationInput) locationInput.value = ''

    const sizeInput = container.querySelector('.new-indication-size')
    if (sizeInput) sizeInput.value = ''

    // Reset selects to first option
    const selects = container.querySelectorAll('select')
    selects.forEach(select => {
      select.selectedIndex = 0
    })
  }

  /**
   * Update the indication count badge for a weld
   */
  updateIndicationCount(weldId, delta) {
    const countBadge = this.element.querySelector(`.badge[data-weld-id="${weldId}"]`)
    if (countBadge) {
      const currentCount = parseInt(countBadge.textContent) || 0
      countBadge.textContent = Math.max(0, currentCount + delta)
    }
  }

  /**
   * Delete indication - show confirmation
   */
  deleteIndication(event) {
    event.preventDefault()
    const indicationId = event.currentTarget.dataset.indicationId
    const weldId = event.currentTarget.dataset.weldId

    this.indicationToDelete = { weldId, indicationId }

    console.log('[rt-welds-tree] Show delete confirmation for indication:', indicationId)

    const modal = document.getElementById('deleteIndicationModal')
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('show')
      } else {
        modal.classList.add('show')
        modal.style.display = 'block'
        document.body.classList.add('modal-open')
      }
    }
  }

  /**
   * Confirm delete indication
   */
  async confirmDeleteIndication(event) {
    event.preventDefault()

    const { weldId, indicationId } = this.indicationToDelete

    if (!indicationId) {
      console.error('[rt-welds-tree] No indication ID to delete')
      return
    }

    console.log('[rt-welds-tree] Deleting indication:', indicationId)

    // Close modal
    this.closeModal('deleteIndicationModal')

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/rt_indications/${indicationId}`,
        {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          }
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds-tree] Indication deleted successfully')
        // Remove the indication row from DOM
        const indicationRow = this.element.querySelector(`tr.indication-row[data-indication-id="${indicationId}"]`)
        if (indicationRow) {
          indicationRow.remove()
        }

        // Update indication count
        const countBadge = this.element.querySelector(`.badge[data-weld-id="${weldId}"]`)
        if (countBadge) {
          const currentCount = parseInt(countBadge.textContent) || 0
          countBadge.textContent = Math.max(0, currentCount - 1)
        }

        // Check if no indications left, show message
        const remainingIndications = this.element.querySelectorAll(`tr.indication-row`)
        if (remainingIndications.length === 0) {
          const indicationsTableBody = this.element.querySelector(`.indications-content[data-weld-id="${weldId}"] tbody`)
          if (indicationsTableBody) {
            // Replace table with no indications message
            indicationsTableBody.innerHTML = `
              <tr>
                <td colspan="7" class="text-center text-muted py-3">
                  <i class="fa fa-check-circle mr-2"></i>
                  No indications recorded for this weld
                </td>
              </tr>
            `
          }
        }
      } else {
        console.error('[rt-welds-tree] Delete failed:', data)
        this.showError(data.message || 'Failed to delete indication')
      }
    } catch (error) {
      console.error('[rt-welds-tree] Delete error:', error)
      this.showError('Network error. Please try again.')
    }

    this.indicationToDelete = { weldId: null, indicationId: null }
  }

  /**
   * Export welds as CSV
   */
  exportCSV(event) {
    event.preventDefault()
    console.log('[rt-welds-tree] Export as CSV')
    // TODO: Implement CSV export
    alert('CSV export coming soon!')
  }

  /**
   * Export welds as Excel
   */
  exportExcel(event) {
    event.preventDefault()
    console.log('[rt-welds-tree] Export as Excel')
    // TODO: Implement Excel export
    alert('Excel export coming soon!')
  }

  /**
   * Close modal helper
   */
  closeModal(modalId) {
    const modal = document.getElementById(modalId)
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('hide')
      } else {
        modal.classList.remove('show')
        modal.style.display = 'none'
        document.body.classList.remove('modal-open')
        // Remove backdrop if exists
        const backdrop = document.querySelector('.modal-backdrop')
        if (backdrop) backdrop.remove()
      }
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (this.hasAlertBoxTarget) {
      this.alertBoxTarget.textContent = message
      this.alertBoxTarget.classList.remove('d-none', 'alert-success')
      this.alertBoxTarget.classList.add('alert-danger')

      // Scroll to error
      this.alertBoxTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })

      // Auto-hide after 5 seconds
      setTimeout(() => this.hideAlert(), 5000)
    } else {
      alert(message)
    }
  }

  /**
   * Show success message
   */
  showSuccess(message) {
    if (this.hasAlertBoxTarget) {
      this.alertBoxTarget.textContent = message
      this.alertBoxTarget.classList.remove('d-none', 'alert-danger')
      this.alertBoxTarget.classList.add('alert-success')

      // Auto-hide after 3 seconds
      setTimeout(() => this.hideAlert(), 3000)
    }
  }

  /**
   * Hide alert message
   */
  hideAlert() {
    if (this.hasAlertBoxTarget) {
      this.alertBoxTarget.classList.add('d-none')
    }
  }

  /**
   * Hide error message (alias for backward compatibility)
   */
  hideError() {
    this.hideAlert()
  }

  /**
   * Get CSRF token
   */
  get csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
